import { ModelRow, ObjectUtil, StringUtil } from "@mcleod/core";
import { ReportPreviewTabSource } from "./ReportPreviewTabSource";
import { Tab } from "../../components/tabset/Tab";
import { ReportData } from "./ReportData";

export class ReportPreviewDataResolver {
    private reportFieldName: string;

    constructor(reportFieldName?: string) {
        this.reportFieldName = !StringUtil.isEmptyString(reportFieldName) ? reportFieldName : "reports";
    }

    /**
     * Resolves various types if input data to display in the ReportPreview page into an array containing some
     * combination of ReportData and Tab objects.
     * - If the supplied input is a ModelRow, get the data from that field.  The data should be a plain 'any' object
     *   that contains an array of report output objects (equivalent to ReportData).
     * - Otherwise, if the supplied input is an array, we'll just loop through that array.
     * - For every item in the array:
     *     - If it's a Tab, then it's a non-report/ad-hoc tab to be displayed as-is.
     *     - If it's a ReportData object, then it's meant to be displayed in a ReportViewerTab.
     *     - If it's a plain/any object, then it's meant to be converted into a ReportData object (and then shown in a
     *       ReportViewerTab).
     * @param input The source/input from which the ReportPreview panel's tabset can be built.
     * @returns An array of ReportPreviewTabSource objects (a combination of ReportData and Tab objects)
     */
    public resolve(input: ModelRow|(any|ReportPreviewTabSource)[]): ReportPreviewTabSource[] {
        if (input == null)
            return [];
        let inputReportData: any;
        if (input instanceof ModelRow) {
            inputReportData = input.get(this.reportFieldName) ?? [];
        }
        else {
            inputReportData = input;
        }
        // inputReportData is an empty, non-array object when no reports were produced, so bail
        if (ObjectUtil.isEmptyObject(inputReportData)) {
            return [];
        }

        return Object.values(inputReportData).map(item => {
            if (item instanceof Tab || item instanceof ReportData) {
                return item;
            }
            const reportData = item as any;
            return new ReportData(reportData.report_name, {
                excelBytes: reportData.excel_output,
                hasExceptions: reportData.has_exceptions,
                pdfBytes: reportData.pdf_output,
                reportName: reportData.report_name,
                reportType: reportData.report_type,
                serverError: reportData.error_info
            });
        });
    }
}
